// LooksPolls.js 

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth }          from '../contexts/AuthContext';
import { db }               from '../utils/firebaseConfig';
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy,
} from 'firebase/firestore';
import { useNavigate }      from 'react-router-dom';

import './Bulletin.css';
import { AiOutlineZoomIn }  from 'react-icons/ai';

import {
  FacebookShareButton,
  WhatsappShareButton,
  TwitterShareButton,
  EmailShareButton,
  FacebookIcon,
  FacebookMessengerIcon,
  WhatsappIcon,
  TwitterIcon,
  EmailIcon,
} from 'react-share';
import LinkIcon from '@mui/icons-material/Link';

const logoSrc = '/looksaway_logo.png';

/* ─────────────────────────────────────────────
   Ultra-lightweight zoom-in modal
────────────────────────────────────────────── */
const ZoomModal = ({ src, onClose }) => {
  const SAFE_MARGIN = 96;
  const BOX_SIZE    = `calc(100vmin - ${SAFE_MARGIN}px)`;

  return (
    <div
      style={{
        position:'fixed', inset:0, background:'rgba(0,0,0,.85)',
        display:'flex', justifyContent:'center', alignItems:'center', zIndex:9999,
      }}
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        onClick={(e)=>e.stopPropagation()}
        style={{ position:'relative', maxWidth:BOX_SIZE, maxHeight:BOX_SIZE }}
      >
        <button
          aria-label="Close image preview"
          onClick={onClose}
          style={{
            position:'absolute', top:8, right:8,
            background:'rgba(0,0,0,.6)', color:'#fff',
            border:'none', borderRadius:'50%', width:32, height:32,
            cursor:'pointer', fontSize:22, lineHeight:0,
          }}
        >
          ×
        </button>
        <img
          src={src}
          alt="Enlarged preview"
          style={{
            maxWidth:'100%', maxHeight:'100%',
            objectFit:'contain', borderRadius:8, display:'block',
          }}
        />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Share-component (unchanged UI)
────────────────────────────────────────────── */
const ShareContainer = () => {
  const [copySuccess, setCopySuccess] = useState('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(()=>{
    const h = ()=>setWindowWidth(window.innerWidth);
    window.addEventListener('resize',h);
    return ()=>window.removeEventListener('resize',h);
  },[]);

  const isWideScreen = windowWidth > 970;
  const iconSize     = isWideScreen ? 40 : 32;

  const containerStyle = {
    backgroundColor:'#fff', borderRadius:8,
    boxShadow:'0 2px 8px rgba(0,0,0,0.05)',
    width:'fit-content', maxWidth:isWideScreen?380:300, textAlign:'center',
    transition:'transform .2s, box-shadow .2s',
    paddingBottom:isWideScreen?10:5, border:'1px solid #d3d3d3',
    display:'flex', flexDirection:'column', alignItems:'center', margin:'20px auto',
  };

  const shareContainerStyle = {
    padding:isWideScreen?'15px 20px':10, backgroundColor:'#f9f9f9',
    borderRadius:8, textAlign:'center', maxWidth:isWideScreen?380:300, margin:'auto',
  };

  const titleStyle = {
    fontSize:isWideScreen?'1.3rem':'1rem',
    marginBottom:isWideScreen?8:12, marginTop:isWideScreen?0:-4,
    fontWeight:'bold',
  };

  const XLogo = ({ size, round }) => (
    <div style={{
      width:size, height:size, background:'#000',
      borderRadius:round?'50%':0, display:'flex',
      justifyContent:'center', alignItems:'center', color:'#fff',
      fontWeight:'bold', fontFamily:'Arial, sans-serif', position:'relative',
    }}>
      <span style={{
        position:'absolute', top:'50%', left:'50%',
        transform:'translate(-50%,-50%)', fontSize:size*0.5, fontWeight:'bold',
      }}>𝕏</span>
    </div>
  );

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(
      ()=>{setCopySuccess('Link copied!'); setTimeout(()=>setCopySuccess(''),2000);},
      ()=>{setCopySuccess('Failed to copy link'); setTimeout(()=>setCopySuccess(''),2000);}
    );
  };

  const renderSmallScreenButtons = () => (
    <>
      <div style={{
        display:'flex', justifyContent:'space-around', width:'100%',
        marginBottom:12, marginTop:8, gap:12,
      }}>
        <FacebookShareButton url={window.location.href}><FacebookIcon size={iconSize} round/></FacebookShareButton>
        <button
          onClick={()=>{const u=encodeURIComponent(window.location.href);window.location.href=`fb-messenger://share/?link=${u}`;}}
          style={{background:'none',border:'none',padding:0,cursor:'pointer'}}
        ><FacebookMessengerIcon size={iconSize} round/></button>
        <WhatsappShareButton url={window.location.href}><WhatsappIcon size={iconSize} round/></WhatsappShareButton>
      </div>
      <div style={{display:'flex',justifyContent:'space-around',width:'100%',gap:12}}>
        <TwitterShareButton url={window.location.href}><XLogo size={iconSize} round/></TwitterShareButton>
        <EmailShareButton url={window.location.href}><EmailIcon size={iconSize} round/></EmailShareButton>
        <button onClick={handleCopyLink}
          style={{background:'none',border:'none',padding:0,cursor:'pointer',display:'flex'}}>
          <LinkIcon style={{fontSize:iconSize}}/>
        </button>
      </div>
    </>
  );

  const renderWideScreenButtons = () => (
    <div style={{
      display:'flex', justifyContent:'space-around', gap:12,
      flexWrap:'wrap', marginTop:10,
    }}>
      <FacebookShareButton url={window.location.href}><FacebookIcon size={iconSize} round/></FacebookShareButton>
      <button
        onClick={()=>{const u=encodeURIComponent(window.location.href);window.location.href=`fb-messenger://share/?link=${u}`;}}
        style={{background:'none',border:'none',padding:0,cursor:'pointer'}}
      ><FacebookMessengerIcon size={iconSize} round/></button>
      <TwitterShareButton url={window.location.href}><XLogo size={iconSize} round/></TwitterShareButton>
      <EmailShareButton url={window.location.href}><EmailIcon size={iconSize} round/></EmailShareButton>
      <button onClick={handleCopyLink}
        style={{background:'none',border:'none',padding:0,cursor:'pointer',display:'flex'}}>
        <LinkIcon style={{fontSize:iconSize}}/>
      </button>
    </div>
  );

  return (
    <div className="submission-container ad-item" style={containerStyle}>
      <div className="share-container" style={shareContainerStyle}>
        <div className="share-title" style={titleStyle}>Share with 3+ People!</div>
        <div className="share-buttons">
          {isWideScreen ? renderWideScreenButtons() : renderSmallScreenButtons()}
        </div>
        {copySuccess && (
          <div style={{
            fontSize:isWideScreen?'1rem':'.8rem', color:'green',
            marginTop:isWideScreen?8:5,
          }}>{copySuccess}</div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   LooksPolls main component
────────────────────────────────────────────── */
const LooksPolls = () => {
  const { currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();

  /* refs & helpers */
  const unsubscribeRef = useRef([]);
  const voteSubsRef    = useRef({ face:new Map(), mc:new Map() });

  const anonIdRef = useRef(
    localStorage.getItem('anonId') ||
      (()=>{const id=crypto.randomUUID?.()||`${Date.now()}-${Math.random()}`;localStorage.setItem('anonId',id);return id;})()
  );
  const effectiveUserId = currentUser ? currentUser.uid : anonIdRef.current;

  /* state */
  const [questions,    setQuestions]    = useState([]);
  const [voteCounts,   setVoteCounts]   = useState({});
  const [userVotes,    setUserVotes]    = useState({});
  const [localCounts,  setLocalCounts]  = useState({});
  const [loading,      setLoading]      = useState(false);
  const [errorMsg,     setErrorMsg]     = useState('');
  const [windowWidth,  setWindowWidth]  = useState(window.innerWidth);
  const [mcQuestions,  setMcQuestions]  = useState([]);
  const [mcVotes,      setMcVotes]      = useState({});
  const [userMcVotes,  setUserMcVotes]  = useState({});
  const [allQuestions, setAllQuestions] = useState([]);
  const [zoomUrl,      setZoomUrl]      = useState(null);

  /* responsive */
  useEffect(()=>{
    const h=()=>setWindowWidth(window.innerWidth);
    window.addEventListener('resize',h);
    return ()=>window.removeEventListener('resize',h);
  },[]);
  const isLarge  = windowWidth >= 1180;
  const isSmall  = windowWidth < 480;
  const isTablet = windowWidth >= 480 && windowWidth < 768;
  const HEADER_TOP_MARGIN = isLarge ? '2rem' : isTablet ? '0rem' : '-1rem';

  /* unsubscribe helpers */
  const addUnsub = fn => unsubscribeRef.current.push(fn);
  const clearOldSubs = ()=>{
    unsubscribeRef.current.forEach(fn=>fn?.());
    unsubscribeRef.current=[];
    voteSubsRef.current.face.clear();
    voteSubsRef.current.mc.clear();
  };

  /* -----------------------------------------------------------
     Firestore listeners   (🔧 UPDATE: always refresh localCounts)
  ----------------------------------------------------------- */
  const listenForVotes = (qId, faceCnt) => {
    if (voteSubsRef.current.face.has(qId)) return;
    const unsub = onSnapshot(collection(db,'faceQuestions',qId,'votes'),snap=>{
      const counts=new Array(faceCnt).fill(0);
      let myIdx=null;
      snap.forEach(d=>{
        const { faceIndex } = d.data();
        if (typeof faceIndex==='number') counts[faceIndex]++;
        if (d.id===effectiveUserId) myIdx=faceIndex;
      });
      setVoteCounts(p=>({...p,[qId]:counts}));
      /* 🔄 always overwrite so we stay up-to-date */
      setLocalCounts(p=>({...p,[qId]:counts}));
      setUserVotes(p=>{
        const c={...p};
        if (myIdx!=null) c[qId]=myIdx; else delete c[qId];
        return c;
      });
    });
    addUnsub(unsub);
    voteSubsRef.current.face.set(qId,unsub);
  };

  const listenForMcVotes = (qId) => {
    if (voteSubsRef.current.mc.has(qId)) return;
    const unsub = onSnapshot(collection(db,'questionAnswers',qId,'votes'),snap=>{
      const votes={};
      let myVote=null;
      snap.forEach(d=>{
        const { answerIndex } = d.data();
        if (typeof answerIndex==='number')
          votes[answerIndex]=(votes[answerIndex]||0)+1;
        if (d.id===effectiveUserId) myVote=answerIndex;
      });
      setMcVotes(p=>({...p,[qId]:votes}));
      /* 🔄 keep in sync */
      setLocalCounts(p=>({...p,[qId]:votes}));
      setUserMcVotes(p=>{
        const u={...p};
        if (myVote!==null) u[qId]=myVote; else delete u[qId];
        return u;
      });
    });
    addUnsub(unsub);
    voteSubsRef.current.mc.set(qId,unsub);
  };

  /* -----------------------------------------------------------
     live question lists   (order ASC like Bulletin.js)
  ----------------------------------------------------------- */
  const fetchQuestions = useCallback(()=>{
    setLoading(true);
    const qRef=query(collection(db,'faceQuestions'),orderBy('createdAt','asc'));
    const unsub = onSnapshot(qRef,snap=>{
      const data=snap.docs.map(d=>({id:d.id,...d.data()}));
      setQuestions(data);
      data.forEach(q=>listenForVotes(q.id,q.faces?.length||0));
      setLoading(false);
    },err=>{
      console.error(err); setErrorMsg('Failed to load questions.'); setLoading(false);
    });
    addUnsub(unsub);
  },[]);

  const fetchMcQuestions = useCallback(()=>{
    const qRef=query(collection(db,'questionAnswers'),orderBy('createdAt','asc'));
    const unsub = onSnapshot(qRef,snap=>{
      const data=snap.docs.map(d=>({id:d.id,...d.data()}));
      setMcQuestions(data);
      data.forEach(q=>listenForMcVotes(q.id));
    },err=>{
      console.error(err); setErrorMsg('Failed to load multiple-choice questions.');
    });
    addUnsub(unsub);
  },[]);

  useEffect(()=>{
    fetchQuestions();
    fetchMcQuestions();
    return ()=>clearOldSubs();
  },[fetchQuestions,fetchMcQuestions]);

  /* combine + order oldest→newest (matches asc) */
  useEffect(()=>{
    const combined=[
      ...questions.map(q=>({...q,type:'face'})),
      ...mcQuestions.map(q=>({...q,type:'multipleChoice'})),
    ];
    combined.sort((a,b)=>{
      const aTime=a.createdAt?.seconds||0;
      const bTime=b.createdAt?.seconds||0;
      return aTime-bTime;
    });
    setAllQuestions(combined);
  },[questions,mcQuestions]);

  /* helpers */
  const isQuestionAnswered = q =>
    q.type==='face' ? userVotes[q.id]!==undefined : userMcVotes[q.id]!==undefined;

  const findFirstUnansweredIndex = ()=>{
    for(let i=0;i<allQuestions.length;i++)
      if(!isQuestionAnswered(allQuestions[i])) return i;
    return allQuestions.length;
  };

  const handleFaceClick = async (qId,idx,countsArr)=>{
    const prevIdx=userVotes[qId];
    const counts=[...(localCounts[qId]||countsArr.map(()=>0))];
    if(prevIdx===idx){
      counts[idx]=Math.max(0,counts[idx]-1);
      setUserVotes(p=>{const c={...p};delete c[qId];return c;});
    }else{
      if(prevIdx!==undefined) counts[prevIdx]=Math.max(0,counts[prevIdx]-1);
      counts[idx]=(counts[idx]||0)+1;
      setUserVotes(p=>({...p,[qId]:idx}));
    }
    setLocalCounts(p=>({...p,[qId]:counts}));
    try{
      const voteRef=doc(db,'faceQuestions',qId,'votes',effectiveUserId);
      if(prevIdx===idx) await deleteDoc(voteRef);
      else await setDoc(voteRef,{faceIndex:idx,votedAt:serverTimestamp()});
    }catch(err){
      console.error(err);
      alert('Could not save your vote.');
    }
  };

  const handleMcClick = async (qId,answerIndex)=>{
    const prevVote=userMcVotes[qId];
    try{
      const voteRef=doc(db,'questionAnswers',qId,'votes',effectiveUserId);
      if(prevVote===answerIndex) await deleteDoc(voteRef);
      else await setDoc(voteRef,{answerIndex,votedAt:serverTimestamp()});
    }catch(err){
      console.error(err);
      alert('Could not save your vote.');
    }
  };

  /* Admin shortcuts */
  const editQ = q         => navigate(`/admin?edit=${q.id}&type=${q.type}`);
  const delQ  = (id,type) => navigate(`/admin?delete=${id}&type=${type}`);

  /* shared constants / builders */
  const BADGE_HEIGHT = 30;
  const bubbleClass  = isSel => (isSel?' selected':'');
  const buildVoteLabel = (count,total,withWord)=>{
    const pct=total?Math.round((count/total)*100):0;
    return `${pct}% (${count}${withWord?' votes':''})`;
  };

  /* styles (identical to before)… */
  const questionBoxStyle = {
    width:isLarge?'100%':'', maxWidth:isLarge?'1600px':'600px',
    minWidth:isLarge?'840px':'', padding:isLarge?'40px 60px':'20px',
    margin:isLarge?'0 auto 50px 0':'0 auto 30px auto',
    boxShadow:isLarge?'0 6px 12px rgba(0,0,0,0.15)':'0 2px 4px rgba(0,0,0,0.1)',
    border:isLarge?'1px solid #e0e0e0':'none', borderRadius:isLarge?'12px':'10px',
    background:'#fafafa', boxSizing:'border-box', position:'relative', overflow:'visible',
  };

  const facesRowStyle = {
    display: isLarge ? 'grid' : 'flex',
    gridTemplateColumns: isLarge ? 'repeat(2, 1fr)' : '',
    gap: isLarge ? '60px' : '10px',
    marginTop: isLarge ? '30px' : '5px',
    justifyContent: 'center',
    flexDirection: !isLarge ? 'column' : 'row',
    alignItems: 'center',
    width: '100%',
    boxSizing: 'border-box',
  };

  const faceCardStyle = {
    width: isLarge ? '384px' : 'auto',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    boxSizing: 'border-box',
    maxWidth: '100%',
  };

  const getMobileSide = (faces) => {
    const count = faces?.length || 0;
    if (isSmall) return count > 1 ? '75vw' : '90vw';
    return count > 1 ? '230px' : '280px';
  };

  const faceImageWrapperStyle = (border, boxShadow, faces) => ({
    border,
    boxShadow,
    margin: 0,
    padding: 0,
    width:  isLarge ? '320px' : getMobileSide(faces),
    height: isLarge ? '320px' : getMobileSide(faces),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    boxSizing: 'border-box',
    cursor: 'pointer',
    transition: 'transform .3s ease, box-shadow .3s ease',
    marginTop: isLarge ? '10px' : '5px',
    lineHeight: 0,
    borderRadius: isLarge ? '12px' : '8px',
  });

  const faceImageStyle = {
    width: '102%',
    height: '102%',
    objectFit: 'cover',
    display: 'block',
    margin: 0,
    padding: 0,
    borderRadius: 0,
    border: 'none',
  };

  /* ────────── progress helpers ────────── */
  const firstUnansweredIndex = findFirstUnansweredIndex();
  const answered = firstUnansweredIndex;
  const total    = allQuestions.length;
  const percent  = total ? Math.round((answered / total) * 100) : 0;

  const MiniProgressBar = () => (
    <div className="mini-progress-wrapper">
      <div className="mini-progress-bar">
        <div
          className="mini-progress-fill"
          style={{ width: `${percent}%` }}
          aria-label={`${percent}% complete`}
        />
      </div>
      <span className="mini-progress-text">
        {answered}/{total}
      </span>
    </div>
  );

  /* ──────────────────────────────────────────
     faceBox (face-comparison renderer)
  ────────────────────────────────────────── */
  const faceBox = (q, idx) => {
    /* ⬇️  prefer always-fresh localCounts */
    const counts     = localCounts[q.id] || voteCounts[q.id] || [];
    const myChoice   = userVotes[q.id];
    const voted      = myChoice !== undefined;

    const faceCount  = q.faces?.length || 0;
    const hasThree   = faceCount === 3;
    const totalVotes = counts.reduce((a,b)=>a+b,0);

    return (
      <div key={q.id} className="face-question-box" style={questionBoxStyle}>
        {/* (admin buttons unchanged)… */}
        {isAdmin && (
          <div
            className="admin-controls"
            style={{
              position:'absolute', bottom:15, left:'50%',
              transform:'translateX(-50%)',
              display:'flex', gap:10, zIndex:999,
              boxShadow:'0 2px 8px rgba(0,0,0,0.2)',
              background:'rgba(255,255,255,0.9)',
              padding:5, borderRadius:4,
            }}
          >
            <button
              onClick={(e)=>{e.stopPropagation();editQ(q);}}
              className="admin-edit-btn"
              style={{
                background:'#4CAF50', color:'#fff', border:'none',
                borderRadius:4, padding:'8px 12px', cursor:'pointer',
                fontSize:14, fontWeight:'bold',
                boxShadow:'0 2px 4px rgba(0,0,0,0.2)',
              }}
            >
              Edit
            </button>
            <button
              onClick={(e)=>{e.stopPropagation();delQ(q.id,'face');}}
              className="admin-delete-btn"
              style={{
                background:'#F44336', color:'#fff', border:'none',
                borderRadius:4, padding:'8px 12px', cursor:'pointer',
                fontSize:14, fontWeight:'bold',
                boxShadow:'0 2px 4px rgba(0,0,0,0.2)',
              }}
            >
              Delete
            </button>
          </div>
        )}

        {/* question heading (unchanged) */}
        <h2 style={{
          marginBottom:isLarge?30:20,
          fontSize:isLarge?'1.5rem':'1.1rem',
          textAlign:'left',
          fontWeight:600,
          color:'#262626',
          lineHeight:1.3,
          maxWidth:'100%',
          margin:'0 0 25px 0',
          padding:'0 0 12px',
          position:'relative',
          letterSpacing:'-0.01em',
          fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif',
        }}>
          {q.questionText}
          <div style={{
            position:'absolute', bottom:0, left:0, right:0,
            height:1, background:'#dbdbdb', borderRadius:1,
          }}/>
        </h2>

        {/* faces grid */}
        <div
          className={`faces-row ${hasThree ? 'three-images' : ''}`}
          style={facesRowStyle}
        >
          {q.faces?.map((face,i)=>{
            const count      = counts[i] || 0;
            const isSelected = myChoice === i;
            const isOurPick  = face.ourPick;
            const isCorrect  = face.isCorrect;

            let border='2px solid #000', boxShadow='none';
            if (voted){
              if (isOurPick)  border='3px solid #28a745';
              if (isCorrect)  border='3px solid #ffc107';
              if (isSelected) boxShadow='0 0 0 3px #1ca9ff';
            }else if(isSelected){
              border='2px solid #1ca9ff';
            }

            const isThird = hasThree && i===2;
            const cardStyle={
              ...faceCardStyle,
              ...(isThird && isLarge ? {gridColumn:'1 / span 2',justifySelf:'center'} : {}),
            };

            return (
              <div key={i} className="face-card" style={cardStyle}>
                {/* badges */}
                <div style={{
                  height:BADGE_HEIGHT,
                  display:'flex',alignItems:'center',justifyContent:'center',gap:10,
                }}>
                  {voted && isOurPick && (
                    <span style={{color:'#28a745',fontWeight:600,fontSize:isLarge?16:14}}>
                      Our Pick
                    </span>
                  )}
                  {voted && isCorrect && (
                    <span style={{color:'#ffc107',fontWeight:600,fontSize:isLarge?16:14}}>
                      Correct
                    </span>
                  )}
                </div>

                {/* clickable image */}
                <div
                  className="face-image-border-wrapper"
                  style={faceImageWrapperStyle(border,boxShadow,q.faces)}
                  onClick={()=>handleFaceClick(q.id,i,counts)}
                >
                  <img
                    src={face.imageUrl}
                    alt={`Face ${i+1}`}
                    className="voting-face-img"
                    style={faceImageStyle}
                  />
                </div>

                {/* zoom icon */}
                {!isSmall && (
                  <div
                    onClick={(e)=>{e.stopPropagation();setZoomUrl(face.imageUrl);}}
                    style={{
                      marginTop:6,width:isLarge?320:280,
                      display:'flex',justifyContent:'flex-end',cursor:'pointer',
                    }}
                    aria-label="Enlarge image"
                  >
                    <AiOutlineZoomIn className="zoom-btn__icon"/>
                  </div>
                )}

                {/* vote label */}
                <div
                  className={
                    'vote-count-label fade-swap' +
                    (voted?'':idx===0?' blurred':' hidden') +
                    bubbleClass(isSelected)
                  }
                  style={{
                    fontSize:isLarge?18:14,
                    marginTop:isLarge?15:8,
                    fontWeight:isLarge?'bold':'normal',
                  }}
                >
                  {buildVoteLabel(count,totalVotes,idx===0)}
                </div>
              </div>
            );
          })}
        </div>

        {idx===0 && !voted && (
          <>
            <div className="fade-swap"
                 style={{
                   textAlign:'center',
                   marginTop:isLarge?30:10,
                   fontStyle:'italic',
                   fontSize:isLarge?18:14,
                   padding:isLarge?'10px 0':0,
                 }}>
              {`What did everyone else pick? ${isLarge?'Click':'Tap'} any image to see!`}
            </div>
            <p className="vote-helper-note"
               style={{
                 fontSize:isLarge?15:13,
                 color:'#666',
                 marginTop:6,
                 textAlign:'center',
               }}>
              * The Top Pick? Behind the Blurred Lines.
            </p>
          </>
        )}
      </div>
    );
  };

  /* ──────────────────────────────────────────
     mcBox (multiple-choice renderer)
  ────────────────────────────────────────── */
  const mcBox = (q, idx) => {
    const userVote   = userMcVotes[q.id];
    const hasVoted   = userVote !== undefined;
    /* ⬇️  always-fresh counts first */
    const votes      = localCounts[q.id] || mcVotes[q.id] || {};
    const totalVotes = Object.values(votes).reduce((a,b)=>a+b,0);

    return (
      <div key={q.id} className="face-question-box" style={questionBoxStyle}>
        {/* admin buttons unchanged */}
        {isAdmin && (
          <div
            className="admin-controls"
            style={{
              position:'absolute', bottom:15, left:'50%',
              transform:'translateX(-50%)',
              display:'flex', gap:10, zIndex:999,
              boxShadow:'0 2px 8px rgba(0,0,0,0.2)',
              background:'rgba(255,255,255,0.9)',
              padding:5, borderRadius:4,
            }}
          >
            <button
              onClick={(e)=>{e.stopPropagation();editQ(q);}}
              className="admin-edit-btn"
              style={{
                background:'#4CAF50', color:'#fff', border:'none',
                borderRadius:4, padding:'8px 12px', cursor:'pointer',
                fontSize:14, fontWeight:'bold',
                boxShadow:'0 2px 4px rgba(0,0,0,0.2)',
              }}
            >
              Edit
            </button>
            <button
              onClick={(e)=>{e.stopPropagation();delQ(q.id,'multipleChoice');}}
              className="admin-delete-btn"
              style={{
                background:'#F44336', color:'#fff', border:'none',
                borderRadius:4, padding:'8px 12px', cursor:'pointer',
                fontSize:14, fontWeight:'bold',
                boxShadow:'0 2px 4px rgba(0,0,0,0.2)',
              }}
            >
              Delete
            </button>
          </div>
        )}

        <h2 style={{
          marginBottom:isLarge?30:20,
          fontSize:isLarge?'1.5rem':'1.1rem',
          textAlign:'left', fontWeight:600, color:'#262626',
          lineHeight:1.3, maxWidth:'100%',
          margin:'0 0 25px 0', padding:'0 0 12px',
          position:'relative', letterSpacing:'-0.01em',
          fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif',
        }}>
          {q.questionText}
          <div style={{
            position:'absolute', bottom:0, left:0, right:0,
            height:1, background:'#dbdbdb', borderRadius:1,
          }}/>
        </h2>

        {/* central image */}
        <div style={{display:'flex',justifyContent:'center',marginBottom:30}}>
          <div
            className="face-image-border-wrapper"
            style={{
              ...faceImageWrapperStyle('2px solid #000','none'),
              width:isLarge?450:250, height:isLarge?450:250,
            }}
          >
            <img
              src={q.imageUrl}
              alt="Question"
              className="voting-face-img"
              style={faceImageStyle}
            />
          </div>
        </div>

        {/* answers grid */}
        <div style={{
          display:'grid',
          gridTemplateColumns:isLarge?'repeat(2,1fr)':'1fr',
          gap:20, width:'100%', maxWidth:800, margin:'0 auto',
        }}>
          {q.answers.map((answer,i)=>{
            const isSelected = userVote === i;
            const voteCount  = votes[i] || 0;

            const text      = typeof answer==='object' ? answer.text      : answer;
            const isOurPick = typeof answer==='object' ? answer.isOurPick : false;
            const isCorrect = typeof answer==='object' ? answer.isCorrect : false;

            let border='2px solid #ddd', background='#f8f8f8';
            if (hasVoted){
              if (isOurPick)  { border='2px solid #28a745'; background='#f0fff0'; }
              if (isCorrect)  { border='2px solid #ffc107'; background='#fffbf0'; }
              if (isSelected) { border='2px solid #1ca9ff'; background='#f0f8ff'; }
            }

            return (
              <div key={i} style={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                <div
                  onClick={()=>handleMcClick(q.id,i)}
                  className="answer-choice"
                  style={{
                    padding:'15px 20px',
                    borderRadius:30, border, background,
                    cursor:'pointer', position:'relative',
                    transition:'transform .3s ease, box-shadow .3s ease',
                    display:'flex', justifyContent:'center', alignItems:'center',
                    minHeight:60, textAlign:'center', width:'100%',
                  }}
                >
                  <div style={{fontSize:isLarge?20:16}}>{text}</div>

                  {hasVoted && (
                    <div style={{position:'absolute',top:-10,right:10,display:'flex',gap:5}}>
                      {isOurPick && (
                        <span style={{
                          background:'#28a745', color:'#fff',
                          padding:'2px 8px', borderRadius:10, fontSize:12,
                        }}>Our Pick</span>
                      )}
                      {isCorrect && (
                        <span style={{
                          background:'#ffc107', color:'#000',
                          padding:'2px 8px', borderRadius:10, fontSize:12,
                        }}>Correct</span>
                      )}
                    </div>
                  )}
                </div>

                <div
                  className={
                    'vote-count-label fade-swap' +
                    (hasVoted?'':idx===0?' blurred':' hidden') +
                    bubbleClass(isSelected)
                  }
                  style={{
                    fontSize:isLarge?18:14,
                    marginTop:isLarge?15:8,
                    fontWeight:isLarge?'bold':'normal',
                  }}
                >
                  {buildVoteLabel(voteCount,totalVotes,idx===0)}
                </div>
              </div>
            );
          })}
        </div>

        {idx===0 && !hasVoted && (
          <div
            className={'fade-swap' + (hasVoted?' hidden':'')}
            style={{
              textAlign:'center',
              marginTop:isLarge?30:10,
              fontStyle:'italic',
              fontSize:isLarge?18:14,
              padding:isLarge?'10px 0':0,
            }}
          >
            {`What did everyone else pick? ${isLarge?'Click':'Tap'} any option to see!`}
          </div>
        )}
      </div>
    );
  };

  /* ────────────────────────── JSX ────────────────────────── */
  return (
    <div className="face-voting-container">
      {/* header (unchanged)… */}
      <div className="page-header" style={{
        width:'100%',
        maxWidth:isLarge?'1600px':'600px',
        minWidth:isLarge?'840px':'',
        padding:isLarge?'0 60px 32px':isTablet?'0 20px 16px':'0 0 16px',
        marginLeft:isLarge?0:'auto',
        marginRight:'auto',
        marginTop:HEADER_TOP_MARGIN,
        boxSizing:'border-box',
        textAlign:isLarge?'left':'center',
        position:'relative',
      }}>
        <div className="header-stack"
              style={{ display:'inline-flex', flexDirection:'column',
                      alignItems:'center', gap:isLarge?'20px':'8px', margin:'0 auto' }}>
          <div className="header-row"
                style={{ display:'flex', flexDirection:isLarge?'row':'column',
                        alignItems:'center', justifyContent:'center',
                        gap:isLarge?'24px':'12px', maxWidth:isLarge?'800px':'100%',
                        margin:'0 auto' }}>
            <img
              src={logoSrc}
              alt="Looksaway logo"
              className="bulletin-logo"
              onClick={()=>window.scrollTo({ top:0, behavior:'smooth' })}
            />
            <div className="intro-explanation"
                  style={{
                    backgroundColor:'#f8f9fa',
                    border:'1px solid #e0e0e0',
                    borderRadius:8,
                    padding:isLarge?'12px 28px':'12px 15px',
                    maxWidth:isLarge?740:'100%',
                    textAlign:isLarge?'left':'center',
                    boxShadow:'0 2px 4px rgba(0,0,0,0.05)',
                    fontSize:isLarge?20:16,
                    lineHeight:1.5,
                    color:'#333',
                  }}>
              {!isLarge && (
                <img
                  src={logoSrc}
                  alt="Looksaway logo"
                  className="bulletin-logo bulletin-logo--inside"
                  onClick={()=>window.scrollTo({ top:0, behavior:'smooth' })}
                />
              )}
              <h1 style={{ margin:0, fontSize:'2.2rem', fontWeight:700 }}>Looksaway</h1>
              <p style={{ margin:0 }}>
                Match Feelings with Faces: pick the face that best fits prompts about behaviours,
                origins, or quoted feelings. After selecting, see what everyone else picked!
                <br/>All faces are AI-generated – not real people.
              </p>
              <p style={{
                marginTop:8,
                fontSize:isLarge?14:12,
                fontStyle:'italic',
                color:'#666',
              }}>
                For entertainment purposes only.
              </p>
            </div>
          </div>
        </div>

        {!loading && total>0 && (
          <div className="progress-wrapper">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width:`${percent}%` }}
                aria-label={`${percent}% complete`}
              />
            </div>
            <span className="progress-text">
              {answered}/{total}
            </span>
          </div>
        )}
      </div>

      {/* status */}
      {loading && <p>Loading questions…</p>}
      {errorMsg && <p style={{color:'red'}}>{errorMsg}</p>}
      {!loading && total===0 && <p>No questions yet.</p>}

      {/* main loop */}
      {!loading && total>0 && allQuestions.map((q,i)=>{
        if (i>firstUnansweredIndex) return null;
        const card = q.type==='face' ? faceBox(q,i) : mcBox(q,i);
        return (
          <React.Fragment key={q.id}>
            {card}
            {(i+1)%4===0 && i<firstUnansweredIndex && <MiniProgressBar/>}
            {(i+1)%3===0 && i<firstUnansweredIndex && (
              <div style={{
                width:'100%',
                display:'flex',
                justifyContent:'center',
                alignItems:'center',
                margin:windowWidth>970 ? '0 auto 50px auto' : '0 auto 30px auto',
                maxWidth:windowWidth>970 ? '1600px':'600px',
                minWidth:windowWidth>970 ? '840px':'',
              }}>
                <ShareContainer/>
              </div>
            )}
          </React.Fragment>
        );
      })}

      {zoomUrl && <ZoomModal src={zoomUrl} onClose={()=>setZoomUrl(null)}/>}
    </div>
  );
};

export default LooksPolls;


